import { Link } from "react-router-dom";
import { ArrowRight, Clock, MapPin } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Seo, SITE_URL } from "@/components/Seo";
import { FaqList, JsonLd, faqJsonLd } from "@/components/FaqList";
import { PhotoHero, Eyebrow, Steps, CallButtons } from "@/components/BodyshopUi";
import { BODYSHOP_IMG, BODYSHOP_SERVICES, CLAIM_STEPS, WHY_US, BODYSHOP_FAQ, WA_BODYSHOP } from "@/lib/bodyshop";

const LD = {
  "@context": "https://schema.org", "@type": "AutoBodyShop", name: "Centro Auto Pontedera - Carrozzeria",
  url: `${SITE_URL}/carrozzeria-pontedera`, telephone: "+39 0587 484791", email: "info@centroautopontedera.it",
  image: BODYSHOP_IMG.hero, foundingDate: "1970",
  address: { "@type": "PostalAddress", streetAddress: "Via Don Primo Mazzolari 34/38", postalCode: "56025", addressLocality: "Pontedera", addressRegion: "PI", addressCountry: "IT" },
  geo: { "@type": "GeoCoordinates", latitude: 43.668493, longitude: 10.658385 },
  openingHours: ["Mo-Fr 08:30-12:30", "Mo-Fr 14:30-18:30"],
  makesOffer: BODYSHOP_SERVICES.map((s) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name: s.title } })),
};
const FAQ_LD = faqJsonLd(BODYSHOP_FAQ);
const STATS = [["Dal 1970", "attività di famiglia"], ["700 mq", "di officina coperta"], ["500 mq", "di parcheggio clienti"], ["Forno", "cabina di verniciatura"]];

const ServiceCard = ({ s, i }) => {
  const body = (
    <>
      <div className="aspect-[4/3] overflow-hidden"><img src={s.img} alt={s.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
      <div className="p-5">
        <h3 className="font-display font-bold text-lg mb-2 flex items-center gap-2">{s.title}{s.to && <ArrowRight className="w-4 h-4 text-[#E63946]" />}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
      </div>
    </>
  );
  const cls = "group block rounded-2xl overflow-hidden bg-[#181A22] border border-white/10 hover:border-[#E63946]/40 transition-colors fade-up";
  return s.to
    ? <Link to={s.to} className={cls} style={{ animationDelay: `${i * 60}ms` }} data-testid={`service-${s.key}`}>{body}</Link>
    : <div className={cls} style={{ animationDelay: `${i * 60}ms` }} data-testid={`service-${s.key}`}>{body}</div>;
};

export default function Carrozzeria() {
  return (
    <div className="min-h-screen bg-background grain" data-testid="carrozzeria-page">
      <Seo title="Carrozzeria a Pontedera (PI) — Verniciatura a forno, grandine, sinistri | Centro Auto Pontedera"
        description="Carrozzeria a Pontedera dal 1970: riparazioni, verniciatura a forno, levabolli e grandine senza verniciatura, gestione sinistri con le assicurazioni e auto sostitutiva."
        path="/carrozzeria-pontedera" image={BODYSHOP_IMG.hero} />
      <JsonLd id="ld-bodyshop" data={LD} />
      <JsonLd id="ld-faq" data={FAQ_LD} />
      <Navbar />
      <PhotoHero id="carrozzeria" img={BODYSHOP_IMG.hero} wa={WA_BODYSHOP} eyebrow="Carrozzeria a Pontedera dal 1970"
        title={<>La tua auto,<br /><span className="amber">come nuova.</span></>}
        text="Riparazioni di carrozzeria, verniciatura a forno, grandine e sinistri. Ci pensiamo noi, anche alla pratica con l'assicurazione e all'auto sostitutiva." />

      <section className="max-w-7xl mx-auto px-6 -mt-10 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="carrozzeria-stats">
          {STATS.map(([n, l]) => (
            <div key={n} className="rounded-2xl glass border border-white/10 p-5">
              <div className="font-display text-lg sm:text-3xl font-extrabold whitespace-nowrap">{n}</div>
              <div className="text-xs sm:text-sm text-muted-foreground mt-1">{l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="carrozzeria-services">
        <Eyebrow>I nostri servizi</Eyebrow>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-8">Tutto per la tua carrozzeria</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {BODYSHOP_SERVICES.map((s, i) => <ServiceCard key={s.key} s={s} i={i} />)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-20" data-testid="carrozzeria-hail-banner">
        <Link to="/riparazione-grandine" className="group grid md:grid-cols-2 rounded-3xl overflow-hidden border border-white/10 bg-[#181A22] hover:border-[#E63946]/40 transition-colors">
          <img src={BODYSHOP_IMG.hailCar} alt="Auto danneggiata dalla grandine" loading="lazy" className="w-full h-64 md:h-full object-cover" />
          <div className="p-8 lg:p-12 flex flex-col justify-center">
            <Eyebrow>Danni da grandine</Eyebrow>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase leading-tight">Grandinata? Togliamo i bolli senza riverniciare</h2>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mt-4">Con la tecnica levabolli la lamiera torna liscia e la vernice originale resta intatta. Ti aiutiamo anche con la polizza eventi atmosferici.</p>
            <span className="inline-flex items-center gap-2 mt-6 text-[#E63946] font-semibold group-hover:gap-3 transition-all">Scopri la riparazione grandine <ArrowRight className="w-4 h-4" /></span>
          </div>
        </Link>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-20">
        <Eyebrow>Sinistri e assicurazioni</Eyebrow>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-8">Hai avuto un incidente? Ecco come funziona</h2>
        <Steps steps={CLAIM_STEPS} id="claim" />
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-20 grid lg:grid-cols-[1fr_1.1fr] gap-10 items-center" data-testid="carrozzeria-why">
        <div className="rounded-3xl overflow-hidden border border-white/10"><img src={BODYSHOP_IMG.paint} alt="Verniciatura a forno" loading="lazy" className="w-full h-full max-h-[480px] object-cover" /></div>
        <div>
          <Eyebrow>Chi siamo</Eyebrow>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-6">Tradizione artigiana, tecnologia moderna</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {WHY_US.map(([t, d]) => (
              <div key={t}><h3 className="font-display font-bold mb-1.5 text-[#E63946]">{t}</h3><p className="text-sm text-muted-foreground leading-relaxed">{d}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-16">
        <Eyebrow>Domande frequenti</Eyebrow>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight uppercase mb-6">Carrozzeria: le domande più comuni</h2>
        <FaqList items={BODYSHOP_FAQ} />
      </section>

      <section className="max-w-7xl mx-auto px-6" data-testid="carrozzeria-contact">
        <div className="rounded-3xl bg-[#181A22] border border-white/10 p-8 lg:p-12 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase">Preventivo gratuito</h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-3 mb-6">Mandaci le foto del danno su WhatsApp o chiamaci: ti rispondiamo con una prima valutazione.</p>
            <CallButtons wa={WA_BODYSHOP} id="carrozzeria-bottom" />
          </div>
          <div className="text-sm text-muted-foreground space-y-3">
            <p className="flex gap-3"><Clock className="w-5 h-5 text-[#E63946] shrink-0" /><span><b className="text-foreground">Lun – Ven</b> 8:30 – 12:30 / 14:30 – 18:30<br /><b className="text-foreground">Sabato</b> su appuntamento</span></p>
            <a href="https://www.google.com/maps/dir/?api=1&destination=Via+Don+Primo+Mazzolari+38,+56025+Pontedera+PI" target="_blank" rel="noopener noreferrer" className="flex gap-3 hover:text-foreground" data-testid="carrozzeria-directions"><MapPin className="w-5 h-5 text-[#E63946] shrink-0" />Via Don Primo Mazzolari 34/38, 56025 Pontedera (PI)</a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
